
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Calendar, User2, ArrowRight, Layers } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO, addDays, isPast } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { TaskDetailsDialog } from "./TaskDetailsDialog";
import { useOrganization } from "@/contexts/OrganizationContext";

interface TaskKanbanBoardProps {
  tasks: any[];
  phases?: any[];
  userRole?: string | null;
  onTaskComplete: (taskId: string) => Promise<void>;
  showOldCompletedTasks?: boolean;
}

export const TaskKanbanBoard = ({ 
  tasks, 
  phases = [],
  userRole, 
  onTaskComplete,
  showOldCompletedTasks = false
}: TaskKanbanBoardProps) => {
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  const statusColumns = [
    { id: 'todo', label: 'To Do' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'on_hold', label: 'On Hold' },
    { id: 'completed', label: 'Completed' }
  ];

  // Fixed filter to correctly handle old completed tasks
  const filterOldCompletedTasks = (task: any) => {
    // If not a completed task or toggle is on, show it
    if (task.status !== 'completed' || showOldCompletedTasks) {
      return true;
    }
    
    // Only hide completed tasks when they're older than a week
    if (!task.updated_at) return true;
    
    const updatedDate = parseISO(task.updated_at);
    const oneWeekAgo = addDays(new Date(), -7);
    
    // Show if task was completed less than a week ago
    return updatedDate > oneWeekAgo;
  };

  // Apply the filter to tasks
  const filteredTasks = tasks.filter(filterOldCompletedTasks);

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      if (!currentOrganization?.id) {
        throw new Error("No organization selected");
      }

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: "update",
          taskId: taskId,
          task: { status: newStatus },
          organizationId: currentOrganization.id
        }
      });

      if (error) throw error;

      toast({
        title: "Task updated",
        description: `Task moved to ${newStatus.replace('_', ' ')}`,
      });
      
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
    }
  };

  const handleTaskClick = (task: any) => {
    setSelectedTask(task);
    setIsDetailsOpen(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500 hover:bg-red-600';
      case 'medium':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'low':
        return 'bg-green-500 hover:bg-green-600';
      default:
        return 'bg-blue-500 hover:bg-blue-600';
    }
  };
  
  // Find phase name for a task
  const getPhaseNameForTask = (task: any) => {
    if (!task.phase_id || !phases.length) return null;
    const phase = phases.find(p => p.id === task.phase_id);
    return phase ? phase.name : null;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {statusColumns.map(column => (
        <div key={column.id} className="flex flex-col space-y-2">
          <div className="bg-background p-2 rounded-t-md border-b-2 font-medium shadow-sm">
            <h3 className="mb-1">{column.label}</h3>
            <div className="text-xs text-muted-foreground">
              {filteredTasks.filter(t => t.status === column.id).length} tasks
            </div>
          </div>
          <div className="bg-muted/30 p-2 rounded-md min-h-[300px] flex-1 space-y-2">
            {filteredTasks.filter(t => t.status === column.id).map(task => (
              <Card key={task.id} className="bg-background shadow-sm">
                <CardHeader className="p-3 pb-1">
                  <div 
                    className="font-medium cursor-pointer" 
                    onClick={() => handleTaskClick(task)}
                  >
                    {task.title}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Badge 
                      variant="secondary" 
                      className={`text-xs text-white ${getPriorityColor(task.priority)}`}
                    >
                      {task.priority}
                    </Badge>
                    
                    {getPhaseNameForTask(task) && (
                      <Badge variant="outline" className="text-xs flex items-center">
                        <Layers className="h-3 w-3 mr-1" />
                        {getPhaseNameForTask(task)}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-1 space-y-1">
                  {task.assignee && (
                    <div className="flex items-center text-xs text-muted-foreground">
                      <User2 className="h-3 w-3 mr-1" />
                      {task.assignee.full_name || task.assignee.email}
                    </div>
                  )}
                  {task.due_date && (
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3 mr-1" />
                      Due: {format(parseISO(task.due_date), 'MMM d, yyyy')}
                    </div>
                  )}
                  <div className="flex justify-between mt-2">
                    {column.id !== 'completed' && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 px-2 text-xs"
                        onClick={() => onTaskComplete(task.id)}
                      >
                        <Check className="h-3 w-3 mr-1" /> Complete
                      </Button>
                    )}
                    {column.id !== 'completed' && column.id !== 'on_hold' && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 px-2 text-xs ml-auto"
                        onClick={() => handleStatusChange(task.id, statusColumns[statusColumns.findIndex(c => c.id === column.id) + 1].id)}
                      >
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
      
      <TaskDetailsDialog
        task={selectedTask}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        userRole={userRole}
      />
    </div>
  );
};
